import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import {useAuth} from '../contexts/AuthContext';
import {useNavigate} from 'react-router-dom';
import {format,subDays,startOfWeek,endOfWeek,startOfMonth,endOfMonth,addDays,addWeeks,addMonths,addYears,subWeeks,subMonths,subYears} from 'date-fns';

// Import chart components - FIXED: Removed non-existent RevenueChartimport 
import SalesChart from '../components/charts/SalesChart';
import PaymentMethodChart from '../components/charts/PaymentMethodChart';
import DoctorPerformanceChart from '../components/charts/DoctorPerformanceChart';

const {FiUsers,FiCalendar,FiDollarSign,FiAlert,FiFlag,FiTrendingUp,FiTrendingDown,FiActivity,FiChevronLeft,FiChevronRight,FiCreditCard,FiClock,FiHome}=FiIcons;

const Dashboard=()=> {
  const [stats,setStats]=useState({});
  const [salesData,setSalesData]=useState({
    daily: [],
    weekly: [],
    monthly: [],
    yearly: [],
    paymentMethods: [],
    doctorPerformance: []
  });
  const [loading,setLoading]=useState(true);
  const [selectedPeriod,setSelectedPeriod]=useState('daily');// daily,weekly,monthly,yearly 
  const [currentPeriodOffset,setCurrentPeriodOffset]=useState(0);// For navigation
  const [chartLoading,setChartLoading]=useState(false);
  const [balances, setBalances] = useState({ Cash: 0, Card: 0, Bank: 0 });
  const [loadingBalances, setLoadingBalances] = useState(true);
  const {user}=useAuth();
  const navigate=useNavigate();

  useEffect(()=> {
    fetchDashboardStats();
    if (user?.role==='admin') {
      fetchSalesAnalytics();
      fetchBalances();
    }
  },[user]);

  const fetchBalances = async () => {
      try {
          setLoadingBalances(true);
          const response = await axios.get('/api/expenses/accounts');
          const balancesData = response.data.reduce((acc, item) => {
              acc[item.account_type] = parseFloat(item.balance);
              return acc;
          }, { Cash: 0, Card: 0, Bank: 0 });
          setBalances(balancesData);
      } catch (error) {
          console.error('Error fetching balances:', error);
          // Set default values if API fails
          setBalances({ Cash: 0, Card: 0, Bank: 0 });
      } finally {
          setLoadingBalances(false);
      }
  };

  // Fetch sales analytics when period or offset changes
  useEffect(()=> {
    if (user?.role==='admin' && (currentPeriodOffset !==0)) {
      fetchSalesAnalytics(selectedPeriod,currentPeriodOffset);
    }
  },[selectedPeriod,currentPeriodOffset,user]);

  const fetchDashboardStats=async ()=> {
    try {
      const response=await axios.get('/api/reports/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:',error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesAnalytics=async (period=null,offset=0)=> {
    try {
      setChartLoading(true);
      const params=new URLSearchParams();
      if (period) {
        params.append('period',period);
        params.append('offset',offset.toString());
      }
      
      const response=await axios.get(`/api/reports/sales-analytics${params.toString() ? '?' + params.toString() : ''}`);
      
      if (period && offset !==0) {
        // Update only the specific period data
        setSalesData(prev=> ({
          ...prev,
          [period]: response.data[period] || []
        }));
      } else {
        // Update all data (initial load)
        setSalesData(response.data);
      }
    } catch (error) {
      console.error('Error fetching sales analytics:',error);
    } finally {
      setChartLoading(false);
    }
  };

  // Helper function to get current period data
  const getCurrentPeriodData=()=> {
    return salesData[selectedPeriod] || [];
  };

  // Helper function to get period label with date restrictions
  const getPeriodLabel=()=> {
    const now=new Date();
    let dateLabel='';
    
    switch (selectedPeriod) {
      case 'daily':
        if (currentPeriodOffset===0) {
          dateLabel='Last 30 Days';
        } else {
          const targetDate=addDays(now,currentPeriodOffset * -30);
          dateLabel=format(targetDate,'MMM yyyy');
        }
        break;
      case 'weekly':
        if (currentPeriodOffset===0) {
          dateLabel='Last 12 Weeks';
        } else {
          const targetDate=addWeeks(now,currentPeriodOffset * -12);
          dateLabel=format(targetDate,'MMM yyyy');
        }
        break;
      case 'monthly':
        if (currentPeriodOffset===0) {
          dateLabel='Last 12 Months';
        } else {
          const targetDate=addMonths(now,currentPeriodOffset * -12);
          dateLabel=format(targetDate,'yyyy');
        }
        break;
      case 'yearly':
        if (currentPeriodOffset===0) {
          dateLabel='Last 10 Years';
        } else {
          const targetDate=addYears(now,currentPeriodOffset * -10);
          dateLabel=format(targetDate,'yyyy');
        }
        break;
      default:
        dateLabel='';
    }
    
    return dateLabel;
  };

  // Check if we can navigate in a direction (with date restrictions)
  const canNavigate=(direction)=> {
    const now=new Date();
    const minDate=new Date('2019-10-01');
    
    if (direction===1) {
      // Previous (going back in time)
      let checkDate;
      switch (selectedPeriod) {
        case 'daily':
          checkDate=addDays(now,(currentPeriodOffset + 1) * -30 - 30);
          break;
        case 'weekly':
          checkDate=addWeeks(now,(currentPeriodOffset + 1) * -12 - 12);
          break;
        case 'monthly':
          checkDate=addMonths(now,(currentPeriodOffset + 1) * -12 - 12);
          break;
        case 'yearly':
          checkDate=addYears(now,(currentPeriodOffset + 1) * -10 - 10);
          break;
        default:
          return false;
      }
      return checkDate >=minDate;
    } else {
      // Next (going forward in time)
      return currentPeriodOffset > 0;// Can only go forward if we're not at current period
    }
  };

  // Navigation handlers with date restrictions
  const navigatePeriod=(direction)=> {
    if (!canNavigate(direction)) return;
    
    const newOffset=currentPeriodOffset + direction;
    setCurrentPeriodOffset(newOffset);
  };

  const resetToCurrentPeriod=()=> {
    setCurrentPeriodOffset(0);
    // Fetch current period data
    fetchSalesAnalytics();
  };

  // UPDATED: Helper function to get current month name
  const getCurrentMonthName=()=> {
    return format(new Date(),'MMMM yyyy');
  };

  // Base stat cards for all users
  const baseStatCards=[ 
    {
      title: 'Total Patients',
      value: stats.total_patients || 0,
      icon: FiUsers,
      color: 'bg-blue-500',
      roles: ['admin','manager','receptionist']
    },
    {
      title: "Today's Appointments",
      value: stats.today_appointments || 0,
      icon: FiCalendar,
      color: 'bg-green-500',
      roles: ['admin','manager','receptionist','doctor']
    },
    {
      title: 'Unpaid Invoices',
      value: stats.unpaid_invoices || 0,
      icon: FiDollarSign,
      color: 'bg-yellow-500',
      roles: ['admin','manager','receptionist']
    },
    {
      title: 'Outstanding Amount',
      value: `QR ${(stats.outstanding_amount || 0).toLocaleString()}`,
      icon: FiAlert,
      color: 'bg-red-500',
      roles: ['admin','manager']
    },
    {
      title: 'Flagged Transactions',
      value: stats.flagged_transactions || 0,
      icon: FiFlag,
      color: 'bg-purple-500',
      roles: ['admin']
    }
  ];

  // Revenue cards for admin users
  const revenueCards=[
    {
      title: "Today's Revenue",
      value: `QR ${(stats.today_revenue || 0).toLocaleString()}`,
      icon: FiDollarSign,
      color: 'bg-emerald-500',
      change: stats.revenue_change_percentage,
      roles: ['admin']
    },
    {
      title: "Today's Revenue (Cash)",
      value: `QR ${(stats.today_cash_revenue || 0).toLocaleString()}`,
      icon: FiDollarSign,
      color: 'bg-cyan-500',
      change: stats.cash_revenue_change,
      roles: ['admin']
    },
    {
      title: "Today's Revenue (Card)",
      value: `QR ${(stats.today_card_revenue || 0).toLocaleString()}`,
      icon: FiCreditCard,
      color: 'bg-indigo-500',
      change: stats.card_revenue_change,
      roles: ['admin']
    },
    {
      title: "Today's Revenue (Credit)",
      value: `QR ${(stats.today_credit_revenue || 0).toLocaleString()}`,
      icon: FiClock,
      color: 'bg-orange-500',
      change: stats.credit_revenue_change,
      roles: ['admin'],
      subtitle: 'Unpaid invoices created today'
    }
  ];

  // Filter cards based on user role
  const firstRowCards=baseStatCards.filter(stat=> stat.roles.includes(user?.role));
  const secondRowCards=user?.role==='admin' ? revenueCards : [];

  const handleQuickAction=(action)=> {
    switch (action) {
      case 'view-appointments':
        navigate('/appointments');
        break;
      case 'add-patient':
        navigate('/patients');
        break;
      case 'view-invoices':
        navigate('/invoices');
        break;
      case 'view-payouts':
        navigate('/doctors');
        break;
      case 'view-reports':
        navigate('/reports');
        break;
      default:
        break;
    }
  };

  const getChangeIcon=(change)=> {
    if (!change) return null;
    return change > 0 ? FiTrendingUp : FiTrendingDown;
  };

  const getChangeColor=(change)=> {
    if (!change) return '';
    return change > 0 ? 'text-green-600' : 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-grey-900">Dashboard</h1>
        <p className="mt-1 text-sm text-grey-600">
          Welcome back,{user?.name}! Here's what's happening at EasyAI Dental Center.
        </p>
      </div>

      {/* First Row - Base Stats (5 cards for admin, varies for others) */}
      <div className={`grid gap-6 ${user?.role === 'admin' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
        {firstRowCards.map((stat,index)=> (
          <motion.div
            key={stat.title}
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{delay: index * 0.1}}
            className="bg-white overflow-hidden shadow rounded-lg"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`${stat.color} rounded-md p-3`}>
                    <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-grey-500 truncate">
                      {stat.title}
                    </dt>
                    <dd className="text-lg font-medium text-grey-900">
                      {stat.value}
                    </dd>
                    {stat.subtitle && (
                      <dd className="text-xs text-grey-500 mt-1">
                        {stat.subtitle}
                      </dd>
                    )}
                    {stat.change !==undefined && (
                      <dd className={`text-sm ${getChangeColor(stat.change)} flex items-center mt-1`}>
                        {getChangeIcon(stat.change) && (
                          <SafeIcon icon={getChangeIcon(stat.change)} className="w-3 h-3 mr-1" />
                        )}
                        {stat.change > 0 ? '+' : ''}{stat.change}% from last period
                      </dd>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Second Row - Revenue Cards (Admin Only) */}
      {user?.role==='admin' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {secondRowCards.map((stat,index)=> (
            <motion.div
              key={stat.title}
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{delay: (firstRowCards.length + index) * 0.1}}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`${stat.color} rounded-md p-3`}>
                      <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-grey-500 truncate">
                        {stat.title}
                      </dt>
                      <dd className="text-lg font-medium text-grey-900">
                        {stat.value}
                      </dd>
                      {stat.subtitle && (
                        <dd className="text-xs text-grey-500 mt-1">
                          {stat.subtitle}
                        </dd>
                      )}
                      {stat.change !==undefined && (
                        <dd className={`text-sm ${getChangeColor(stat.change)} flex items-center mt-1`}>
                          {getChangeIcon(stat.change) && (
                            <SafeIcon icon={getChangeIcon(stat.change)} className="w-3 h-3 mr-1" />
                          )}
                          {stat.change > 0 ? '+' : ''}{stat.change}% from last period
                        </dd>
                      )}
                    </dl>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Account Balances - Only for Admin */}
      {user?.role === 'admin' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <motion.div 
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.3, delay: 0.1}}
            className="p-6 rounded-lg shadow bg-green-100/60"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-grey-600">Cash Balance</p>
                {loadingBalances ? (
                  <div className="h-8 w-24 bg-gray-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-grey-900">
                    QR {parseFloat(balances.Cash || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              <div className="p-3 rounded-full text-green-800 bg-opacity-20">
                <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-800" />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.3, delay: 0.2}}
            className="p-6 rounded-lg shadow bg-blue-100/60"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-grey-600">Card Balance</p>
                {loadingBalances ? (
                  <div className="h-8 w-24 bg-gray-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-grey-900">
                    QR {parseFloat(balances.Card || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              <div className="p-3 rounded-full text-blue-800 bg-opacity-20">
                <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-blue-800" />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.3, delay: 0.3}}
            className="p-6 rounded-lg shadow bg-purple-100/60"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-grey-600">Bank Balance</p>
                {loadingBalances ? (
                  <div className="h-8 w-24 bg-gray-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-grey-900">
                    QR {parseFloat(balances.Bank || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              <div className="p-3 rounded-full text-purple-800 bg-opacity-20">
                <SafeIcon icon={FiHome} className="w-6 h-6 text-purple-800" />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Sales Analytics Section - Only for Admin */}
      {user?.role==='admin' && (
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.3}}
          className="bg-white shadow rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-grey-900">Sales Analytics</h3>
              <p className="text-sm text-grey-600">Revenue trends and performance metrics</p>
            </div>
            <div className="flex space-x-2">
              {['daily','weekly','monthly','yearly'].map((period)=> (
                <button
                  key={period}
                  onClick={()=> {
                    setSelectedPeriod(period);
                    setCurrentPeriodOffset(0);// Reset to current period when switching
                    fetchSalesAnalytics();// Fetch current data
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    selectedPeriod===period
                      ? 'bg-maroon-600 text-white'
                      : 'bg-grey-100 text-grey-700 hover:bg-grey-200'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={()=> navigatePeriod(1)}
                disabled={!canNavigate(1)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  !canNavigate(1)
                    ? 'text-grey-400 bg-grey-50 cursor-not-allowed'
                    : 'text-grey-700 bg-grey-100 hover:bg-grey-200'
                }`}
              >
                <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
                Previous
              </button>
              <div className="text-sm font-medium text-grey-900">
                {getPeriodLabel()}
              </div>
              <button
                onClick={()=> navigatePeriod(-1)}
                disabled={!canNavigate(-1)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  !canNavigate(-1)
                    ? 'text-grey-400 bg-grey-50 cursor-not-allowed'
                    : 'text-grey-700 bg-grey-100 hover:bg-grey-200'
                }`}
              >
                Next
                <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-1" />
              </button>
            </div>
            {currentPeriodOffset !==0 && (
              <button
                onClick={resetToCurrentPeriod}
                className="px-3 py-2 text-sm font-medium text-maroon-600 bg-maroon-50 rounded-md hover:bg-maroon-100 transition-colors"
              >
                Back to Current
              </button>
            )}
          </div>

          {/* Revenue Chart */}
          <div className="mb-8">
            <h4 className="text-md font-medium text-grey-800 mb-4">Revenue Trends</h4>
            <div className="h-80 relative">
              {chartLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon-600"></div>
                </div>
              )}
              <SalesChart data={getCurrentPeriodData()} period={selectedPeriod} />
            </div>
          </div>

          {/* UPDATED: Current Month Analytics Section */}
          <div className="border-t border-grey-200 pt-8">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-grey-900 mb-2">Current Month Analytics</h3>
              <p className="text-sm text-grey-600">Performance metrics for {getCurrentMonthName()}</p>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Payment Methods Chart */}
              <div>
                <h4 className="text-md font-medium text-grey-800 mb-4">Payment Methods</h4>
                <div className="h-64">
                  <PaymentMethodChart data={salesData.paymentMethods} />
                </div>
              </div>

              {/* Doctor Performance Chart */}
              <div>
                <h4 className="text-md font-medium text-grey-800 mb-4">Doctor Performance</h4>
                <div className="h-64">
                  <DoctorPerformanceChart data={salesData.doctorPerformance} />
                </div>
              </div>
            </div>

            {/* UPDATED: Revenue Summary Cards - Current Month Data */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <SafeIcon icon={FiDollarSign} className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-green-800">Total Revenue ({getCurrentMonthName()})</p>
                    <p className="text-2xl font-bold text-green-900">
                      QR {(salesData.totalRevenueCurrentMonth || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <SafeIcon icon={FiUsers} className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-blue-800">Avg. Transaction Value</p>
                    <p className="text-2xl font-bold text-blue-900">
                      QR {(salesData.avgTransactionValue || 0).toFixed(0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <SafeIcon icon={FiActivity} className="w-8 h-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-purple-800">Monthly Growth Rate</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {(salesData.growthRate || 0) > 0 ? '+' : ''}{(salesData.growthRate || 0).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <motion.div
          initial={{opacity: 0,x: -20}}
          animate={{opacity: 1,x: 0}}
          transition={{delay: 0.4}}
          className="bg-white shadow rounded-lg p-6"
        >
          <h3 className="text-lg font-medium text-grey-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button
              onClick={()=> handleQuickAction('view-appointments')}
              className="w-full text-left px-4 py-3 bg-maroon-50 hover:bg-maroon-100 rounded-lg transition-colors"
            >
              <div className="font-medium text-maroon-700">View Appointments</div>
              <div className="text-sm text-maroon-600">
                {user?.role==='doctor' ? 'Check your appointment schedule' : 'Manage patient appointments'}
              </div>
            </button>

            {['admin','manager','receptionist'].includes(user?.role) && (
              <button
                onClick={()=> handleQuickAction('add-patient')}
                className="w-full text-left px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <div className="font-medium text-blue-700">Add New Patient</div>
                <div className="text-sm text-blue-600">Register a new patient</div>
              </button>
            )}

            {['admin','manager','receptionist'].includes(user?.role) && (
              <button
                onClick={()=> handleQuickAction('view-invoices')}
                className="w-full text-left px-4 py-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors"
              >
                <div className="font-medium text-yellow-700">Manage Invoices</div>
                <div className="text-sm text-yellow-600">View and manage patient invoices</div>
              </button>
            )}

            {['admin','doctor'].includes(user?.role) && (
              <button
                onClick={()=> handleQuickAction('view-payouts')}
                className="w-full text-left px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
              >
                <div className="font-medium text-purple-700">
                  {user?.role==='doctor' ? 'View My Payouts' : 'Doctor Payouts'}
                </div>
                <div className="text-sm text-purple-600">
                  {user?.role==='doctor' ? 'Check your earnings' : 'Manage doctor payments'}
                </div>
              </button>
            )}

            {user?.role==='admin' && (
              <button
                onClick={()=> handleQuickAction('view-reports')}
                className="w-full text-left px-4 py-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
              >
                <div className="font-medium text-green-700">Analytics & Reports</div>
                <div className="text-sm text-green-600">View detailed analytics and export reports</div>
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;